import { html } from "core/Controls/ControlBand.html";
import { type CollectionView, type Event, EventListener, type Transform, View, type ViewData } from "silver";
import { generateId } from "shared/functions";
import { type ContextualPopup, ContextualPopupBehavior } from "shared/Components/ContextualPopup";

export interface ControlBandControl {
	element: HTMLElement;
}

export interface ControlBandDataSource<C extends ControlBandControl, S = number> {
	getControls(...source: S[]): C[];
}

interface ControlBandItemViewData<C extends ControlBandControl> extends ViewData {
	control: C;
}

type ControlBandItemView<C extends ControlBandControl> = View<ControlBandItemViewData<C>>

interface ControlBandPageViewData<C extends ControlBandControl> extends ViewData {
	controls: Array<ControlBandItemViewData<C>>;
}

interface ControlBandPageView<C extends ControlBandControl> extends View<ControlBandPageViewData<C>> {
	controls: CollectionView<ControlBandItemViewData<C>, ControlBandItemView<C>>;
}

interface ControlBandViewData<C extends ControlBandControl> extends ViewData {
	title: string;
	titleId: string;
	selectId: string|null;
	emptyMessage: string;
	nextButtonLabel: string;
	empty: boolean;
	overflows: boolean;
	selectOptions: ControlBandOptionViewData[];
	pages: { [ key: string ]: ControlBandPageViewData<C> };
	itemsPerPage: number;
	isTransitioning: boolean;
	isLoading: boolean;
	modifierClass: string;
	showTooltip: boolean;
}

interface ControlBandOptionViewData extends ViewData {
	value: string;
	text: string;
}

interface ControlBandView<C extends ControlBandControl> extends View<ControlBandViewData<C>> {
	select: HTMLSelectElement;
	selectOptions: CollectionView<ControlBandOptionViewData>;
	pages: CollectionView<ControlBandPageViewData<C>>;
	overflow: HTMLDivElement;
	tooltipButton: HTMLDivElement;
}

export interface ControlBandItemEvent<C extends ControlBandControl> extends Event {
	control: C;
}

export interface ControlBandSelectedOptionChangeEvent extends Event {
	value: string;
	text?: string;
}

export class ControlBand<C extends ControlBandControl, S = number> {

	public readonly onSelectedOptionChange = EventListener.create<ControlBandSelectedOptionChangeEvent>("selectedOptionChange");
	public readonly onRemoveItem = EventListener.create<ControlBandItemEvent<C>>("removeItem");

	private view: ControlBandView<C>;
	private index: number = 0;
	private timeToPaginate: number = 500;
	private redrawOnComplete: boolean = false;
	private sourceData: S[] = [];

	constructor(
		private readonly dataSource: ControlBandDataSource<C, S>,
		private readonly transform: Transform,
		modifierClass: string,
		private readonly standardWidthItemsPerPage: number,
		private readonly standardWidthItemWidth: number = 200
	) {
		this.view = new View<ControlBandViewData<C>>(html, {
			handleNextButtonClick: (): void => {
				this.advance().catch(console.error);
			},
			handleSelectChange: (): void => {
				const select = this.view.select;
				const value = select.value;
				const text = select.options.hasOwnProperty(select.selectedIndex) ? select.options[ select.selectedIndex ].text : undefined;
				this.onSelectedOptionChange.trigger({ value, text });
			},
			handleRemovePage: (page: ControlBandPageView<C>): void => {
				page.controls.forEach((control: ControlBandItemView<C>) => {
					this.onRemoveItem.trigger({
						control: control.get("control")
					});
				});
			}
		}, {
			title:           "",
			titleId:         generateId("controlband-title-"),
			selectId:        null,
			emptyMessage:    "",
			nextButtonLabel: "",
			pages:           { },
			overflows:       false,
			isTransitioning: false,
			selectOptions:   [],
			isLoading:       false,
			modifierClass,
			empty:           true,
			itemsPerPage:    standardWidthItemsPerPage,
			showTooltip:     false,
			tooltipTitle:    "",
			tooltipText:     ""
		}) as ControlBandView<C>;
	}

	public setTitle(title: string): void {
		this.view.set({ title });
	}

	public setEmptyMessage(emptyMessage: string): void {
		this.view.set({ emptyMessage });
	}

	public setNextButtonLabel(nextButtonLabel: string): void {
		this.view.set({ nextButtonLabel });
	}

	public get element(): HTMLElement {
		return this.view.element;
	}

	private get itemsPerPage(): number {
		return this.view.dataset.itemsPerPage;
	}

	private getPageKey(index: number): string {
		return `${index}-${this.standardWidthItemsPerPage}`;
	}

	public advance(): Promise<void> {
		if (this.view.dataset.isTransitioning) {
			throw new Error("Busy");
		}
		if (this.sourceData.length < this.standardWidthItemsPerPage) {
			throw new Error("Cannot advance");
		}

		this.pushPage(this.nextIndex);
		this.view.dataset.isTransitioning = true;

		this.translatePageX(this.index, 0, 0);
		this.translatePageX(this.nextIndex, this.width, 0);

		return new Promise((resolve) => {
			setTimeout(() => {
				this.translatePageX(this.index, -1 * this.width, this.timeToPaginate);
				this.translatePageX(this.nextIndex, 0, this.timeToPaginate);
			}, 0);

			setTimeout(() => {
				this.view.pages.remove(this.getPageKey(this.index));
				this.index = this.nextIndex;
				this.view.dataset.isTransitioning = false;
				if (this.redrawOnComplete) {
					this.resetBands();
					this.redrawOnComplete = false;
				}
				resolve();
			}, this.timeToPaginate + 100);
		});
	}

	private translatePageX(pageIndex: number, xPos: number, duration: number): void {
		const page = this.view.pages.get(this.getPageKey(pageIndex));
		this.transform.setDuration(page.element, duration);
		if (xPos === 0) {
			this.transform.setTransform(page.element, ["none"]);
		} else {
			this.transform.setTransform(page.element, [this.transform.translate(xPos, 0)]);
		}
	}

	private pushPage(idIndex: number): void {
		this.view.pages.add(this.getPageKey(idIndex), this.getPageControls(idIndex));
	}

	private getPageControls(idIndex: number): ControlBandPageViewData<C> {
		const sourcePage: S[] = [];
		for (let i: number = 0; i < Math.min(this.itemsPerPage, this.sourceData.length); i++) {
			const index: number = (i + idIndex) % this.sourceData.length;
			sourcePage.push(this.sourceData[index]);
		}
		const controls = this.dataSource.getControls(...sourcePage);
		return {
			controls: controls.map((control: C) => {
				return { control };
			})
		};
	}

	public handleResize(): void {
		const itemsPerPage = Math.round(this.width / this.standardWidthItemWidth);
		const changes = itemsPerPage != this.itemsPerPage;
		this.view.set({
			itemsPerPage,
			overflows: this.sourceData.length > itemsPerPage
		});
		if (!changes) {
			return;
		}
		this.resetBands();
	}

	public setOptions(options: { [ value: string ]: string }, selectedOption?: string): void {
		this.view.selectOptions.empty();
		this.view.set({ selectId: generateId("controlband-select-") });
		for (const value in options) {
			if (!options.hasOwnProperty(value)) {
				continue;
			}
			this.view.selectOptions.add(value, { value, text: options[ value ] });
		}
		if (selectedOption) {
			this.view.select.value = selectedOption;
		}
	}

	public setContextualPopup(popup: ContextualPopup): void {
		this.view.dataset.showTooltip = true;
		popup.setContext(this.view.tooltipButton, ContextualPopupBehavior.CLICK);
	}

	private get width(): number {
		return this.view.element.offsetWidth;
	}

	private get nextIndex(): number {
		return (this.index + this.itemsPerPage) % this.sourceData.length;
	}

	private clear(): boolean {
		if (this.view.get("isTransitioning")) {
			this.redrawOnComplete = true;
			return false;
		}
		this.view.pages.empty();
		return true;
	}

	private resetBands(): void {
		if (!this.clear()) {
			return;
		}
		this.view.pages.empty();
		this.pushPage(this.index);
	}

	public setSourceData(sourceData: S[]): void {
		this.sourceData = sourceData;
		this.index = 0;
		this.view.set({
			isLoading: false,
			empty:     this.sourceData.length === 0,
			overflows: this.sourceData.length > this.itemsPerPage
		});
		this.resetBands();
	}

	public async setSourceDataAsync(sourceDataPromise: Promise<S[]>): Promise<void> {
		this.view.set({
			isLoading:       true,
			isTransitioning: false,
			overflows:       false
		});
		this.setSourceData(await sourceDataPromise);
	}
}
